// ========================================
// UTILITÁRIO PARA APLICAR CORES DINÂMICAS
// ========================================

/**
 * Aplica as cores do tema dinamicamente baseado nos dados do loginData
 * @param {Object} colorData - Objeto com as cores fornecidas pelo backend
 */
export const applyDynamicColors = (colorData) => {
  if (!colorData) {
    console.warn('Color data not provided, using default colors');
    return;
  }

  const root = document.documentElement;
  
  // Mapeamento das cores do backend para as variáveis CSS
  const colorMapping = {
    // Cores primárias
    '--primary-color': colorData.primaryColor || colorData.primary || '#eb8022',
    '--primary-color-hover': colorData.primaryHover || colorData.primary_hover || '#d26c14',
    '--primary-color-light': colorData.primaryLight || colorData.primary_light || '#f4ac42',
    
    // Cores secundárias
    '--secondary-color': colorData.secondaryColor || colorData.secondary || '#2a1002',
    '--secondary-color-hover': colorData.secondaryHover || colorData.secondary_hover || '#3d2110',
    
    // Cores de fundo
    '--bg-primary': colorData.backgroundColor || colorData.background || '#ffffff',
    '--bg-secondary': colorData.backgroundSecondary || colorData.background_secondary || '#fdf6ef',
    
    // Cores de texto
    '--text-primary': colorData.textPrimary || colorData.text_primary || '#241610',
    '--text-secondary': colorData.textSecondary || colorData.text_secondary || '#7d6a5c',
    '--text-accent': colorData.accentColor || colorData.primaryColor || colorData.primary || '#eb8022',
    
    // Cores de status (opcionais, com fallback)
    '--success-color': colorData.successColor || colorData.success || '#1e8f52',
    '--warning-color': colorData.warningColor || colorData.warning || '#b7950b',
    '--error-color': colorData.errorColor || colorData.error || '#c0392b',
    '--info-color': colorData.infoColor || colorData.info || '#2f7fb8',
  };

  // Aplica as cores às variáveis CSS
  Object.entries(colorMapping).forEach(([property, value]) => {
    if (value) {
      root.style.setProperty(property, value);
    }
  });


  // Calcula e aplica cores derivadas automaticamente
  applyDerivedColors(colorData);
};

/**
 * Calcula e aplica cores derivadas (transparências, gradientes, etc.)
 * @param {Object} colorData - Objeto com as cores fornecidas pelo backend
 */
const applyDerivedColors = (colorData) => {
  const root = document.documentElement;
  const primaryColor =
    colorData.primary ||
    colorData.primary_color ||
    colorData.primaryColor ||
    '#eb8022';
  
  // Converte hex para RGB para criar transparências
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb = hexToRgb(primaryColor);
  if (rgb) {
    // O canal RGB da primaria alimenta os rgba() escritos direto no SCSS.
    // Sem ele, uma conta de paleta azul continuaria com transparencias
    // laranjas: o hex trocaria e os fundos e fios, nao.
    root.style.setProperty('--primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);

    // Transparencias derivadas. As opacidades acompanham os valores de
    // _dynamic-variables.scss -- se divergirem, a paleta gerada fica mais
    // pesada que o padrao da Vitz na mesma tela.
    root.style.setProperty('--bg-accent', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05)`);
    root.style.setProperty('--bg-accent-hover', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.09)`);
    root.style.setProperty('--bg-header', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.07)`);
    root.style.setProperty('--border-primary', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.16)`);
    root.style.setProperty('--border-secondary', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.24)`);
    root.style.setProperty('--border-light', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.09)`);

    // Sombras discretas: a hierarquia vem da tipografia, nao do relevo.
    root.style.setProperty('--shadow-primary', '0 1px 2px rgba(36, 22, 16, 0.04)');
    root.style.setProperty('--shadow-secondary', '0 1px 2px rgba(36, 22, 16, 0.05)');
    root.style.setProperty('--shadow-hover', `0 4px 14px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.14)`);
    root.style.setProperty('--shadow-button', 'none');
    root.style.setProperty('--shadow-overlay', '0 18px 48px rgba(36, 22, 16, 0.16)');
  }
};

/**
 * Aplica as cores do loginData automaticamente
 * @param {Object} loginData - Dados do login que contêm as cores
 */
export const applyColorsFromLoginData = (loginData) => {
  if (!loginData) {
    return;
  }


  // Verifica se existe loginData.colors
  if (loginData.colors) {
    applyDynamicColors(loginData.colors);
    return;
  }

};

/**
 * Reseta as cores para os valores padrão
 */
export const resetToDefaultColors = () => {
  const root = document.documentElement;
  
  // Remove todas as propriedades customizadas
  const customProperties = [
    '--primary-color', '--primary-color-hover', '--primary-color-light',
    '--secondary-color', '--secondary-color-hover',
    '--bg-primary', '--bg-secondary', '--bg-accent', '--bg-accent-hover',
    '--bg-header',
    '--border-primary', '--border-secondary', '--border-light',
    '--text-primary', '--text-secondary', '--text-accent',
    '--success-color', '--warning-color', '--error-color', '--info-color',
    '--shadow-primary', '--shadow-secondary', '--shadow-hover', '--shadow-button',
    '--shadow-overlay', '--primary-rgb'
  ];

  customProperties.forEach(property => {
    root.style.removeProperty(property);
  });
};

/**
 * Hook para aplicar cores automaticamente quando o componente monta
 * @param {Object} loginData - Dados do login
 */
export const useDynamicColors = (loginData) => {
  const applyColors = () => {
    if (loginData) {
      applyColorsFromLoginData(loginData);
    }
  };

  return { applyColors, resetToDefaultColors };
};

/**
 * Função global para aplicar cores - pode ser chamada de qualquer lugar
 * @param {Object} colorData - Dados das cores
 */
export const applyColorsGlobally = (colorData) => {
  applyDynamicColors(colorData);
};

/**
 * Função para aplicar cores do localStorage automaticamente
 */
export const applyColorsFromStorage = () => {
  try {
    const loginData = JSON.parse(localStorage.getItem('login-data'));
    if (loginData) {
      applyColorsFromLoginData(loginData);
    } else {
      console.warn('No login data found in localStorage');
    }
  } catch (error) {
    console.error('Error applying colors from storage:', error);
  }
};

/**
 * Função para definir cores no loginData.colors
 * @param {Object} colors - Objeto com as cores
 */
export const setColorsInLoginData = (colors) => {
  try {
    const loginData = JSON.parse(localStorage.getItem('login-data')) || {};
    loginData.colors = colors;
    localStorage.setItem('login-data', JSON.stringify(loginData));
    applyColorsFromLoginData(loginData);
  } catch (error) {
    console.error('Error setting colors in loginData:', error);
  }
};

/**
 * Função para obter cores do loginData.colors
 */
export const getColorsFromLoginData = () => {
  try {
    const loginData = JSON.parse(localStorage.getItem('login-data'));
    return loginData?.colors || null;
  } catch (error) {
    console.error('Error getting colors from loginData:', error);
    return null;
  }
};

// Torna as funções disponíveis globalmente para debug
if (typeof window !== 'undefined') {
  window.applyColorsGlobally = applyColorsGlobally;
  window.applyColorsFromStorage = applyColorsFromStorage;
  window.resetToDefaultColors = resetToDefaultColors;
  window.setColorsInLoginData = setColorsInLoginData;
  window.getColorsFromLoginData = getColorsFromLoginData;
}
